import React, { useState } from "react";
import { Download, FileSpreadsheet, Plus, RefreshCw } from "lucide-react";
import { showToast } from "@/redux/Toast/actions";
import { useDispatch } from "react-redux";
import { exportRowsToCsv, exportRowsToExcel } from "@/utils/tableExport";
import ReportAnimatedTitle from "@/components/reports/layout/ReportAnimatedTitle";
import { useTranslation } from "react-i18next";
import {
    accentIconText,
    accentIconWrap,
    maintenanceBtnPrimary,
    maintenanceBtnSecondary,
    maintenanceHeaderExportBtn,
    maintenanceHeaderIconBtn,
    maintenanceMutedText,
    maintenanceShell,
    maintenanceShellTopBar,
} from "./maintenanceTheme";

/**
 * Header card for System Maintenance pages — aligned with Import Reports ReportPageHeader.
 */
const MaintenanceHeader = ({
    icon: Icon,
    title,
    subtitle,
    breadcrumb,
    accent = "blue",
    addLabel,
    onAdd,
    addTestId,
    onRefresh,
    exportConfig,
    extraActions,
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [exporting, setExporting] = useState(false);
    const iconWrapClass = accentIconWrap[accent] || accentIconWrap.blue;
    const iconTextClass = accentIconText[accent] || accentIconText.blue;

    const handleCsvExport = () => {
        if (!exportConfig) return;
        try {
            const rows = exportConfig.getRows?.() || [];
            if (!rows.length) {
                dispatch(showToast(t('maintenance.messages.noRecordsToExport'), "info"));
                return;
            }
            exportRowsToCsv({
                fileName: exportConfig.fileName,
                columns: exportConfig.columns,
                rows,
            });
        } catch (err) {
            console.error("CSV export failed", err);
            dispatch(showToast(t('maintenance.messages.exportCsvFailed'), "error"));
        }
    };

    const handleExcelExport = async () => {
        if (!exportConfig || exporting) return;
        setExporting(true);
        try {
            const rows = exportConfig.getRows?.() || [];
            if (!rows.length) {
                dispatch(showToast(t('maintenance.messages.noRecordsToExport'), "info"));
                return;
            }
            await exportRowsToExcel({
                fileName: exportConfig.fileName,
                sheetName: exportConfig.sheetName || exportConfig.fileName,
                columns: exportConfig.columns,
                rows,
            });
            dispatch(showToast(t('maintenance.messages.exportedToExcel'), "success"));
        } catch (err) {
            console.error("Excel export failed", err);
            dispatch(showToast(t('maintenance.messages.exportExcelFailed'), "error"));
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className={maintenanceShell}>
            <div className={maintenanceShellTopBar}>
                <div className="flex min-w-0 items-center gap-3">
                    <div className={iconWrapClass}>
                        {Icon ? (
                            <Icon className={`w-5 h-5 ${iconTextClass}`} strokeWidth={2.2} />
                        ) : null}
                    </div>
                    <div className="min-w-0">
                        {(breadcrumb ?? t('maintenance.systemMaintenance')) ? (
                            <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                                {breadcrumb ?? t('maintenance.systemMaintenance')}
                            </p>
                        ) : null}
                        <ReportAnimatedTitle title={title} />
                        {subtitle ? (
                            <p
                                className={`mt-0.5 text-[12px] hidden sm:block truncate max-w-md lg:max-w-xl ${maintenanceMutedText}`}
                            >
                                {subtitle}
                            </p>
                        ) : null}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 justify-end">
                    {extraActions}

                    {onRefresh ? (
                        <button
                            type="button"
                            onClick={onRefresh}
                            title={t('maintenance.refresh')}
                            className={maintenanceHeaderIconBtn}
                        >
                            <RefreshCw className="h-4 w-4" />
                        </button>
                    ) : null}

                    {exportConfig ? (
                        <>
                            <button
                                type="button"
                                onClick={handleCsvExport}
                                title={t('maintenance.exportCsv')}
                                className={maintenanceBtnSecondary}
                            >
                                <Download className="h-4 w-4" />
                                <span className="hidden sm:inline">{t('maintenance.export')}</span>
                            </button>
                            <button
                                type="button"
                                onClick={handleExcelExport}
                                disabled={exporting}
                                title={t('maintenance.exportToExcel')}
                                className={maintenanceHeaderExportBtn}
                            >
                                <FileSpreadsheet className="h-4 w-4" />
                                {exporting ? t('maintenance.exporting') : t('maintenance.exportToExcel')}
                            </button>
                        </>
                    ) : null}

                    {onAdd ? (
                        <button
                            type="button"
                            onClick={onAdd}
                            data-testid={addTestId}
                            className={maintenanceBtnPrimary}
                        >
                            <Plus className="h-4 w-4" />
                            <span>{addLabel || t('maintenance.addNew')}</span>
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default MaintenanceHeader;
